/**
 * Serialize/deserialize + legacy migration for the FK Breadcrumb navigation
 * tree. Split out of `breadcrumbTree.ts` to keep both files under the 200-line
 * CLAUDE.md cap. `breadcrumbTree.ts` re-exports these so callers can keep
 * importing everything from one place.
 *
 * The persisted shape is identical to the in-memory shape because both
 * `nodes` (`Record<id, NavigationNode>`) and the arrays inside survive
 * `JSON.stringify`/`JSON.parse` round-trips.
 */

import type {
  NavigationNode,
  NavigationNodeId,
  NavigationTree,
  TableExplorerTabContent,
} from '../../../core/types/workspaceTypes';
import { activePathFromRoot, initTree, makeRowNode, makeTableNode, push } from './breadcrumbTree';

/**
 * Slice 6 — cap on the serialized tree depth (per spec's risk register).
 * Trees deeper than this serialize only the active path; off-path branches
 * are dropped to keep session JSON bounded.
 */
export const SERIALIZED_TREE_DEPTH_CAP = 32;

/** Serialize to a JSON-safe object. The shape matches in-memory because all fields are JSON-safe. */
export function serialize(tree: NavigationTree): NavigationTree {
  // Structural clone via JSON to ensure no Map / Date / etc. sneak in.
  return JSON.parse(JSON.stringify(tree)) as NavigationTree;
}

/**
 * Slice 6 — JSON-safety check for a row-node's PK. Numbers / strings /
 * booleans / null survive a session round-trip; binary blobs / functions /
 * symbols don't. Returns `true` when every PK value is JSON-safe.
 */
function isJsonSafePk(pk: ReadonlyArray<{ readonly column: string; readonly value: unknown }>): boolean {
  for (const { value } of pk) {
    if (value === null) continue;
    const t = typeof value;
    if (t === 'string' || t === 'number' || t === 'boolean') continue;
    return false;
  }
  return true;
}

/**
 * Slice 6 — produce a session-safe form of a NavigationTree:
 *  - Beyond `SERIALIZED_TREE_DEPTH_CAP` (32 nodes on the active path), drop
 *    off-path branches so the persisted JSON stays bounded.
 *  - Row-nodes with unsupported PK shapes are stripped of their `pk` field
 *    and demoted to `displayLabel` only — the deserializer prunes them on
 *    load (the chip would re-run a failing fetch otherwise).
 */
export function serializeForSession(tree: NavigationTree): NavigationTree {
  const path = activePathFromRoot(tree);
  const overCap = path.length > SERIALIZED_TREE_DEPTH_CAP;
  const keepIds = overCap ? new Set(path.map((n) => n.id)) : null;

  const nextNodes: Record<NavigationNodeId, NavigationNode> = {};
  for (const [id, node] of Object.entries(tree.nodes)) {
    if (keepIds && !keepIds.has(id)) continue;
    if (node.kind === 'row' && !isJsonSafePk(node.pk)) {
      const stripped: NavigationNode = {
        ...node,
        pk: [],
        // Preserve `displayLabel` if present so the chip still has a label,
        // even though it'll be pruned by the deserializer.
      };
      nextNodes[id] = stripped;
      continue;
    }
    // Drop child-references that point at trimmed-away nodes.
    if (keepIds) {
      nextNodes[id] = {
        ...node,
        childIds: node.childIds.filter((cid) => keepIds.has(cid)),
      } as NavigationNode;
      continue;
    }
    nextNodes[id] = node;
  }
  return {
    rootId: tree.rootId,
    activeNodeId: tree.activeNodeId,
    nodes: nextNodes,
  };
}

/**
 * Slice 6 — deserialize a session-saved tree. Beyond shape validation
 * (already done by `deserialize`), this strips row-nodes with empty PKs
 * (the serializer's "unsupported PK" signal) and re-points the active
 * node + parent's childIds away from any pruned ids. Returns null when
 * pruning would leave the tree empty.
 */
export function deserializeFromSession(payload: unknown): NavigationTree | null {
  const tree = deserialize(payload);
  if (!tree) return null;

  // Identify ids to drop — only row-nodes whose pk is empty count as
  // "unsupported" (root + table nodes never carry pk).
  const dropIds = new Set<NavigationNodeId>();
  for (const [id, node] of Object.entries(tree.nodes)) {
    if (node.kind === 'row' && node.pk.length === 0) dropIds.add(id);
  }
  if (dropIds.size === 0) return tree;
  if (dropIds.has(tree.rootId)) return null; // root invalid → clear tree

  // Collect every node whose ancestor chain hits a dropped node — they go too.
  const droppedSubtree = new Set<NavigationNodeId>(dropIds);
  let added = true;
  while (added) {
    added = false;
    for (const [id, node] of Object.entries(tree.nodes)) {
      if (droppedSubtree.has(id)) continue;
      if (node.parentId && droppedSubtree.has(node.parentId)) {
        droppedSubtree.add(id);
        added = true;
      }
    }
  }

  const nextNodes: Record<NavigationNodeId, NavigationNode> = {};
  for (const [id, node] of Object.entries(tree.nodes)) {
    if (droppedSubtree.has(id)) continue;
    nextNodes[id] = {
      ...node,
      childIds: node.childIds.filter((cid) => !droppedSubtree.has(cid)),
    } as NavigationNode;
  }
  const activeNodeId = droppedSubtree.has(tree.activeNodeId)
    ? tree.rootId
    : tree.activeNodeId;
  return { rootId: tree.rootId, activeNodeId, nodes: nextNodes };
}

/** Deserialize from a JSON-safe object. */
export function deserialize(payload: unknown): NavigationTree | null {
  if (!payload || typeof payload !== 'object') return null;
  const candidate = payload as Partial<NavigationTree>;
  if (
    typeof candidate.rootId !== 'string' ||
    typeof candidate.activeNodeId !== 'string' ||
    typeof candidate.nodes !== 'object' ||
    candidate.nodes === null
  ) {
    return null;
  }
  if (!candidate.nodes[candidate.rootId] || !candidate.nodes[candidate.activeNodeId]) {
    return null;
  }
  return {
    rootId: candidate.rootId,
    activeNodeId: candidate.activeNodeId,
    nodes: { ...candidate.nodes } as Record<NavigationNodeId, NavigationNode>,
  };
}

/**
 * Backward-compat migrator from Slice 1's ad-hoc shape
 * (`viewMode: 'single-row' | 'grid'`, `singleRowRef`) to a 2-node tree.
 *
 * Returns the migrated `navigationTree`, or `null` if no migration is needed.
 */
export function migrateLegacyViewMode(
  content: Pick<TableExplorerTabContent, 'viewMode' | 'singleRowRef' | 'schema' | 'tableName' | 'navigationTree'>,
): NavigationTree | null {
  if (content.navigationTree) return null; // already migrated
  if (content.viewMode !== 'single-row') return null;
  const ref = content.singleRowRef;
  if (!ref || ref.pk.length === 0) return null;

  const root = makeTableNode({
    schema: content.schema,
    table: content.tableName,
    filters: [],
  });
  const tree = initTree(root);
  const child = makeRowNode({
    schema: content.schema,
    table: content.tableName,
    pk: ref.pk,
  });
  const { tree: pushed } = push(tree, child);
  return pushed;
}
